/**
 * Represents the accessors for a singleton instance.
 *
 * @template Singleton The type of the singleton instance.
 */
type SingletonAccessors<Singleton> = {
    /**
     * Sets the value of the singleton instance.
     *
     * @param value The new value for the singleton.
     */
    set: (value: Singleton) => void;
    /**
     * Gets the current value of the singleton instance.
     *
     * @template Override Optional type to override the return type.
     * @returns The current value of the singleton, cast to the Override type if specified.
     */
    get: <Override = Singleton>() => Override;
};

/**
 * Creates a singleton instance with getter and setter accessors.
 *
 * This function provides a way to create and manage a singleton object,
 * allowing for type-safe getting and setting of its value.
 *
 * @template Singleton The type of the singleton instance.
 * @param {Singleton | null} defaultValue The initial value for the singleton. Defaults to null.
 * @returns {SingletonAccessors<Singleton>} An object containing `get` and `set` methods to access and modify the singleton.
 *
 * @example
 * // Create a singleton for a configuration object
 * const configSingleton = createSingleton<{ apiUrl: string }>();
 *
 * // Set the singleton value
 * configSingleton.set({ apiUrl: 'https://api.example.com' });
 *
 * // Get the singleton value
 * const config = configSingleton.get();
 * console.log(config.apiUrl); // 'https://api.example.com'
 *
 * @example
 * // Using with type override
 * interface ExtendedConfig extends Config {
 *   additionalField: string;
 * }
 * const extendedConfig = configSingleton.get<ExtendedConfig>();
 */
export function createSingleton<Singleton>(
    defaultValue: Singleton | null = null,
): SingletonAccessors<Singleton> {
    const singletonRegistry = {
        current: defaultValue,
    };

    return {
        set: (singleton: Singleton): void => {
            singletonRegistry.current = singleton;
        },
        get: <Override = Singleton>(): Override => singletonRegistry.current as unknown as Override,
    };
}
